import { PostCodeRaw } from 'postcodesio-client';
import { Observable } from 'rxjs';
import {
  CamelCasedPropertiesDeep,
  Except,
  LiteralUnion,
  Merge,
} from 'type-fest';

export type Obj<T = unknown> = Record<string, T>;
export type Unit = Record<string, never>;
export type X = unknown;

export type Unit$ = Observable<Unit>;
export type Blank$ = Observable<void | undefined>;
export type Never$ = Observable<never>;
export type NotUndefined<T> = T extends undefined ? never : T;
export type IsAny<T> = 0 extends 1 & T ? true : false;

export type Opt<T> = T | undefined;
export type Func<R = unknown, P extends never[] = never[]> = (
  ...args: P
) => R;
export type NString = string | null | undefined; // Nullable string
export type StringOr<T extends string> = LiteralUnion<T, string>;
export type Falsy = false | 0 | '' | null | undefined;

export type ListId = string | number;
export type NameValueListItems<T extends ListId = ListId> = readonly {
  id: T;
  name: string;
  value: string | number;
}[];

export type Classes<T extends string> = Partial<Record<T, string>>;

export type LocationObj = {
  pathname: string;
  query?: Obj<string>;
};

export type ReactRouterHistoryLocationObject =
  | string
  | { pathname: string; search: string };

export type Dims = {
  width: number;
  height: number;
};

export type ScanWindowOffsets = {
  top: number;
  left: number;
  bottom: number;
  right: number;
};

export enum QuaggaBarcodeType {
  CODE_128 = 'code_128_reader',
  EAN = 'ean_reader',
  EAN_8 = 'ean_8_reader',
  CODE_39 = 'code_39_reader',
  CODE_39_VIN = 'code_39_vin_reader',
  CODABAR = 'codabar_reader',
  UPC = 'upc_reader',
  UPC_E = 'upc_e_reader',
  I2OF5 = 'i2of5_reader',
  _2OF5 = '2of5_reader',
  CODE_93 = 'code_93_reader',
  CODE_32 = 'code_32_reader',
}

export type ErrorMsg = string;

export type GeneralComponent =
  | React.ComponentType
  | React.Component
  | React.FC
  | ((props: never) => unknown);

export type PropsOf<T extends GeneralComponent> = T extends React.FC<
  infer P
>
  ? P
  : T extends React.ComponentType<infer P>
  ? P
  : T extends React.Component<infer P>
  ? P
  : T extends (props: infer P) => unknown
  ? P
  : never;

export type BasicProps = {
  children: React.ReactNode;
  className?: string;
};

export type ForwardedRef<T> = Parameters<
  React.ForwardRefRenderFunction<T, unknown>
>[1];

type StripNull<T> = T extends null ? never : T;
type StripNullFromValues<T> = { [key in keyof T]: StripNull<T[key]> };
type ExtendsField<Base, Condition> = {
  [P in NonNullable<
    {
      [Key in keyof Base]: Condition extends Base[Key] ? Key : never;
    }[keyof Base]
  >]: Base[P];
};
export type ShallowMapFieldsNullToOpt<T> = T extends
  | unknown[]
  | (() => unknown)
  | Exclude<T, Obj<unknown>>
  ? T
  : Merge<
      Partial<StripNullFromValues<ExtendsField<T, null>>>,
      Except<T, keyof ExtendsField<T, null>>
    >;

export type PostCode = ShallowMapFieldsNullToOpt<
  CamelCasedPropertiesDeep<PostCodeRaw>
>;

export type TransientPick<T, K extends keyof T & string> = {
  [key in K as `$${key}`]: T[key];
};

export type TransientExcept<
  T extends Obj,
  K extends keyof T & string
> = {
  [key in keyof T & string as key extends K
    ? never
    : `$${key}`]: T[key];
};

export type NoId<T extends Record<'id', number>> = Except<T, 'id'>;

export type StartsWith<
  T,
  S extends string
> = T extends `${S}${string}` ? T : never;
export type EndsWith<T, E extends string> = T extends `${string}${E}`
  ? T
  : never;
export type DoesntEndWith<
  T,
  E extends string
> = T extends `${string}${E}` ? never : T;

export type Diff<T extends Obj, V extends Obj> = {
  [P in Exclude<keyof T, keyof V>]: T[P];
};

type OnlyNullableParts<T> = T extends NonNullable<T> ? never : T;

export const isNonNullable = <T>(input: T): input is NonNullable<T> =>
  !(input === null || input === undefined);

export const isTruthy = <T>(x: T | Falsy): x is T => !!x;
export const isNullable = <T>(
  input: T
): input is OnlyNullableParts<T> =>
  input === null || input === undefined;

export const hasNoParams = <P, R>(
  input: ((params: P) => R) | ((params: void) => R)
): input is (params: void) => R => input.length === 0;

export const isInArray = <T, A extends T>(
  item: T,
  array: ReadonlyArray<A>
): item is A => array.includes(item as A);

const asEnum = <
  T extends { [key: string]: string },
  K extends keyof T & string
>(
  value: `${T[K]}`,
  enumObject: T
): T[K] => {
  if (Object.values(enumObject).includes(value)) {
    return value as unknown as T[K];
  } else {
    throw new Error('Value provided was not found in Enum');
  }
};

export const tryAsEnum = <
  T extends { [key: string]: string },
  K extends keyof T & string
>(
  value: string,
  enumObject: T
): T[K] => asEnum(value as `${T[K]}`, enumObject);

export const asEnumOrUndefined = <
  T extends { [key: string]: string },
  K extends keyof T & string
>(
  value: string,
  enumObject: T
): Opt<T[K]> => {
  try {
    return tryAsEnum(value, enumObject);
  } catch (e) {
    return undefined;
  }
};

export const isEnumMember = <
  T extends { [key: string]: string },
  K extends keyof T & string
>(
  value: unknown,
  enumObject: T
): value is T[K] =>
  typeof value === 'string' &&
  asEnumOrUndefined(value, enumObject) !== undefined;
